/* Browser-local homepage page sequence. Native page URLs and the opt-in interests view stay independent. */
(() => {
  'use strict';
  if (window.__ixHomePageRotationBound || window.Shopify?.designMode) return;
  window.__ixHomePageRotationBound = true;

  const selector = '[data-ix-page-rotation]';
  let entry = 0;
  let controller = null;
  let timer = 0;
  let root = null;
  let firstPageNodes = null;

  const number = (value) => /^\d+$/.test(String(value)) ? Number(value) : 0;
  const bounds = () => Math.max(1, Math.min(30, number(root?.dataset.pageCount)));
  const storageKey = () => 'indexes_home_page_v1:' + (root.dataset.pageTheme || 'theme') + ':' + (root.dataset.pageCollection || 'all') + ':' + root.dataset.pageSize;
  const cancel = () => {
    entry += 1;
    controller?.abort();
    controller = null;
    window.clearTimeout(timer);
    root?.removeAttribute('aria-busy');
  };
  const save = (page) => {
    try { window.localStorage.setItem(storageKey(), String(page)); return true; }
    catch (_) { return false; }
  };
  const paintFirst = () => {
    if (!firstPageNodes || !root?.isConnected) return;
    root.replaceChildren(...firstPageNodes);
    root.dataset.pageCurrent = '1';
    document.dispatchEvent(new CustomEvent('indexes:products-rendered'));
  };
  const cleanPagination = (container) => {
    container.querySelectorAll('.ix-home-pagination a[href]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.href);
      if (url.origin !== window.location.origin) return;
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      link.href = url.pathname + url.search + url.hash;
    });
  };

  async function enter(isReload = false) {
    root = document.querySelector(selector);
    if (!root || window.Shopify?.designMode) return;
    cancel();
    const ticket = entry;
    const url = new URL(window.location.href);
    const parameter = root.dataset.pageParam || 'page';
    // Alternate views and editor/section responses are not homepage entries.
    if (url.searchParams.has('view') || url.searchParams.has('section_id') || url.searchParams.has('sections')) return;
    const explicitPage = url.searchParams.has(parameter) || url.searchParams.has('page');
    const explicitReload = explicitPage && isReload === true;
    // Native page selections/history remain exact. Only an actual browser reload
    // advances a selected page, not a later pageshow from that same document.
    if (explicitPage && !explicitReload) {
      const current = number(root.dataset.pageCurrent);
      if (current >= 1) save(current);
      root.dataset.rotationState = 'manual';
      return;
    }
    if (!firstPageNodes && number(root.dataset.pageCurrent) === 1) firstPageNodes = Array.from(root.childNodes);
    if (!firstPageNodes && !explicitReload) return;
    const syncReloadUrl = (page) => {
      if (!explicitReload) return true;
      // Keep the URL aligned with what is displayed, so consecutive refreshes
      // advance from this tab's actual page while preserving other query/hash data.
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set(parameter, String(page));
      if (parameter !== 'page' && nextUrl.searchParams.has('page')) nextUrl.searchParams.set('page', String(page));
      try { window.history.replaceState(window.history.state, '', nextUrl.toString()); return true; }
      catch (_) { return false; }
    };

    let previous = 0;
    let storageAvailable = true;
    try {
      const raw = window.localStorage.getItem(storageKey());
      previous = explicitReload ? number(root.dataset.pageCurrent) : number(raw);
      if (!Number.isSafeInteger(previous) || previous > bounds()) previous = 0;
      // Check both read and write availability before choosing another page.
      window.localStorage.setItem(storageKey(), String(previous));
    } catch (_) {
      storageAvailable = false;
    }
    const target = !storageAvailable || previous >= bounds() ? 1 : previous + 1;
    if (target === number(root.dataset.pageCurrent)) {
      if (storageAvailable && !save(target)) { paintFirst(); storageAvailable = false; }
      syncReloadUrl(number(root.dataset.pageCurrent));
      root.dataset.rotationState = storageAvailable ? 'ready' : 'storage-unavailable';
      return;
    }
    if (target === 1 && firstPageNodes) {
      paintFirst();
      if (storageAvailable && !save(1)) storageAvailable = false;
      syncReloadUrl(1);
      root.dataset.rotationState = storageAvailable ? 'ready' : 'storage-unavailable';
      return;
    }

    controller = new AbortController();
    const signal = controller.signal;
    root.setAttribute('aria-busy', 'true');
    root.dataset.rotationState = 'loading';
    url.searchParams.set(parameter, String(target));
    url.searchParams.set('section_id', root.dataset.sectionId);
    url.hash = '';
    timer = window.setTimeout(() => controller?.abort(), 5000);
    try {
      const response = await fetch(url.toString(), { credentials: 'same-origin', signal });
      if (!response.ok) throw new Error('Page unavailable');
      const html = await response.text();
      if (ticket !== entry || signal.aborted || !root.isConnected) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector(selector);
      if (!next || next.dataset.sectionId !== root.dataset.sectionId ||
          number(next.dataset.pageCurrent) !== target || number(next.dataset.pageCount) < target ||
          !next.querySelector('.ix-product-grid article[data-product-id]')) throw new Error('Invalid product page');
      cleanPagination(next);
      if (storageAvailable && !save(target)) {
        paintFirst();
        root.dataset.rotationState = 'storage-unavailable';
        return;
      }
      if (!syncReloadUrl(target)) {
        if (storageAvailable) save(number(root.dataset.pageCurrent));
        root.dataset.rotationState = 'history-unavailable';
        return;
      }
      root.replaceChildren(...Array.from(next.childNodes));
      root.dataset.pageCurrent = String(target);
      root.dataset.pageCount = next.dataset.pageCount;
      if (target === 1) firstPageNodes = Array.from(root.childNodes);
      root.dataset.rotationState = storageAvailable ? 'ready' : 'storage-unavailable';
      document.dispatchEvent(new CustomEvent('indexes:products-rendered'));
    } catch (_) {
      if (ticket === entry) {
        // Failed/slow requests retain the usable server-rendered page.
        paintFirst();
        root.dataset.rotationState = 'fallback';
      }
    } finally {
      if (ticket === entry) {
        window.clearTimeout(timer);
        controller = null;
        root.removeAttribute('aria-busy');
      }
    }
  }

  // No focus/visibility timer changes a page during browsing. A restored BFCache
  // document counts once as a fresh home entry; the initial pageshow does not.
  window.addEventListener('pageshow', (event) => { if (event.persisted) enter(); });
  window.addEventListener('pagehide', cancel);
  const stopLateReplacement = () => {
    if (!controller) return;
    cancel();
    root.dataset.rotationState = 'interaction-kept';
  };
  ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((type) => {
    document.addEventListener(type, stopLateReplacement, { capture: true, passive: true });
  });
  const boot = () => {
    let isReload = false;
    try {
      isReload = window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'reload' ||
        window.performance?.navigation?.type === 1;
    } catch (_) { /* Unknown navigation types retain native manual pagination. */ }
    enter(isReload);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
